// @flow

import moment from 'moment'

/**
 * Time utilities
 * @module util/time
 */

/** Standard labels for hours and minutes */
export const HOUR_LABEL = 'h'
export const MINUTE_LABEL = 'm'

export type ProtobufTimestamp = {|
  seconds: number,
  nanos: number
|}

/**
 * @typedef {object} TimeObject
 * @description Object representation of time
 * @property {number} hour - Hour
 * @property {number} minute - Minute
 * @property {number} second - Second
 * @property {number} millis - Millisecond
 */

/**
 * @typedef {object} DateObject
 * @description Object representation of a date
 * @property {number} year - Year
 * @property {number} month - Month
 * @property {number} day - Day of month
 */

export type TimeObject = {|
  hour: number,
  minute: number,
  second: number,
  millis: number
|}

/** A JSONSchema definition for a date object */
export const DateObjectSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    year: {
      type: 'number'
    },
    month: {
      type: 'number'
    },
    day: {
      type: 'number'
    }
  },
  required: ['year', 'month', 'day'],
  additionalProperties: false
}

export type DateObject = {
  day: number,
  month: number,
  year: number
}

export type DateOrTimeObject = DateObject | TimeObject

// Comparison /////////////////////////////////////////////////////////////////

/**
 * Sort DateObjects
 * @param  {object} a - DateObject
 * @param  {object} b - DateObject
 * @return {-1|0|1} - Sort order
 */
export const dateObjectSort = (a: DateObject, b: DateObject): -1 | 0 | 1 => {
  /** Sort order */
  let sort: -1 | 0 | 1 = 0

  // Same yaer and month, sort by day
  if (a.year === b.year && a.month === b.month) {
    if (a.day < b.day) {
      sort = -1
    } else if (a.day > b.day) {
      sort = 1
    }
  } else if (a.year === b.year) {
    // Same year, sort by month
    if (a.month < b.month) {
      sort = -1
    } else if (a.month > b.month) {
      sort = 1
    }
  } else {
    // Different years, sort by year
    if (a.year < b.year) {
      sort = -1
    } else if (a.year > b.year) {
      sort = 1
    }
  }

  return sort
}

/**
 * Sort TimeObjects
 * @param  {object} a - TimeObject
 * @param  {object} b - TimeObject
 * @return {-1|0|1} - Sort order
 */
export const timeObjectSort = (a: TimeObject, b: TimeObject): -1 | 0 | 1 => {
  /** Sort order */
  let order: -1 | 0 | 1 = 0

  if (a.hour < b.hour) {
    order = -1
  } else if (a.hour > b.hour) {
    order = 1
  } else {
    // hours are the same
    if (a.minute < b.minute) {
      order = -1
    } else if (a.minute > b.minute) {
      order = 1
    }
  }

  return order
}

// Other //////////////////////////////////////////////////////////////////////

/**
 * Regex for a YYYY-MM-DD date string
 */
export const dateStringRegex: RegExp = /^\d{4}-\d{2}-\d{2}$/

/**
 * Convert DateObject to date string (YYYY-MM-DD)
 * @param  {object} date - DateObject to convert
 * @return {string} - Date string (YYYY-MM-DD)
 */
export const convertDateObjectToDateString = (date: DateObject): string =>
  `${date.year}-${date.month}-${date.day}`

export const createHourMinuteString = (minutes: number) =>
  minutes >= 60
    ? `${Math.floor(minutes / 60)}${HOUR_LABEL} ${minutes % 60}${MINUTE_LABEL}`
    : `${minutes}${MINUTE_LABEL}`

/**
 * create a moment object in the controller's time zone from an ambiguous time object
 * @param {string} timeZone   the controller's timeZone from Store
 * @param {Object} timeObject (optional) an object representing an abstract/ambiguous point
 * in time; defaults to now if not supplied
 * @return {Object} a Moment.js object
 */
export const createControllerMoment = (
  timeZone: string,
  timeObject?: DateOrTimeObject | string | number
): moment => {
  return timeObject ? moment.tz(timeObject, timeZone) : moment.tz(timeZone)
}

type DateRange = { start: typeof moment, end: typeof moment }
export const createDateRange = (
  startDate: typeof moment,
  endDate: typeof moment
): DateRange => ({
  start: startDate.startOf('day'),
  end: endDate.endOf('day')
})

/**
 * Create a display-able string from information about a time
 * @param  {string|null} relativeTo - 'SUNRISE'|'SUNSET'|null
 * @param  {module:util/expression~ExpressionType} expressionType - 'EQUALS', 'IN', etc
 * @param  {TimeObject} timeObj - Time object
 * @return {string} - Displayable time string
 * @example
 *    Start at 3:00pm
 *    Start at 3 hours before sunrise
 *    Start at 3 hours after sunrise
 *    Start at sunrise
 *    End before 3:00pm
 *    End before 3 hours before sunrise
 *    End before 3 hours after sunrise
 *    End before sunrise
 *    [EXPRESSION_TYPE] [TIME_OBJ] [RELATIVE_TO]
 */
export const createRuntimeStr = (
  relativeTo: ?string,
  expressionType: string,
  timeObj: Object
) => {
  let str = ''
  let date

  // Expression type
  if (expressionType === 'EQUALS') {
    str += 'Start At'
  } else if (expressionType === 'LESS_THAN') {
    str += 'End Before'
  }

  // Time
  if (timeObj) {
    if (!relativeTo) {
      date = new Date(getDateObjectFromTime(timeObj))
      str += ' ' + moment(date).format('h:mm a')
    } else {
      if (Math.abs(timeObj.hour) > 0) {
        str += ' ' + Math.abs(timeObj.hour) + ' hours'
        if (timeObj.hour > 0) {
          str += ' after'
        } else if (timeObj.hour < 0) {
          str += ' before'
        }
      }
    }
  }

  // Relative to
  if (relativeTo) {
    str += ' ' + (relativeTo.charAt(0).toUpperCase() + relativeTo.slice(1))
  }

  return str
}

/**
 * Create time object
 * @param  {number} [hour=0]   - Hours
 * @param  {number} [minute=0] - Minutes
 * @param  {number} [second=0] - Seconds
 * @param  {number} [millis=0] - Milliseconds
 * @return {TimeObject}        - Time object
 */
export const createTimeObject = (
  hour: number = 0,
  minute: number = 0,
  second: number = 0,
  millis: number = 0
) => ({
  hour: hour,
  minute: minute,
  second: second,
  millis: millis
})

/**
 * Create time object from js date
 * @param  {date} date - Date to create time object from
 * @return {TimeObject} - Time object from passed in `date`
 */
export const getTimeObjectFromDate = (date: any): TimeObject => {
  const momentDate = moment(date)

  return {
    hour: momentDate.hours(),
    minute: momentDate.minutes(),
    second: momentDate.seconds(),
    millis: momentDate.milliseconds()
  }
}

type DateObjToYMDStr = DateObject => string // ex. 1985-07-24
export const dateObjToYMDStr: DateObjToYMDStr = ({ year, month, day }) =>
  `${year}-${month.toString().padStart(2, '0')}-${day
    .toString()
    .padStart(2, '0')}`

/**
 * Create js date from time object
 * @param  {TimeObject} timeObj - Time object to create date from
 * @return {date} - Date created from passed in time object
 */
export const getDateObjectFromTime = (timeObj: Object) => {
  const momentDate = moment()
  const time = timeObj || {}

  momentDate.hours(time.hour || 0)
  momentDate.minutes(time.minute || 0)
  momentDate.seconds(time.second || 0)
  momentDate.milliseconds(time.millis || 0)

  return momentDate.toDate()
}

/**
 * Create a proto compatible DateObject from a standard JS Date
 * @param  {Date} date - JS date (or anything `moment` can parse)
 * @return {DateObject} - Proto compatible date
 */
export const getProtoDateObjectFromDate = (date: any) => {
  const momentDate = moment(date)
  return getProtoDateObjectFromMomentInstance(momentDate)
}

/**
 * Create a proto compatible DateObject from a Moment instance
 * this way we can encapsulate the conversion with out making extra moment calls
 * for code that needs to do date manipulation before converting it to a ProtoDateObject
 * @param  {moment} momentObject - a moment instance
 * @return {DateObject} - Proto compatible date
 */
export const getProtoDateObjectFromMomentInstance = (momentObject: any) => {
  return {
    year: momentObject.year(),
    // XXX: INCREMENTING MONTH
    month: momentObject.month() + 1, // Convert from zero-based index
    day: momentObject.date()
  }
}

type MomentToDateObject = (Object, number) => DateObject
/**
 * Build a date object from a moment
 * @param  {Object} momentObject - a moment instance
 * @param {number} monthOffset - adjust the month to account for different apis
 * @return {DateObject} - { year, month, day }
 */
export const momentToDateObject: MomentToDateObject = (
  momentObject,
  monthOffset = 0
) => ({
  day: momentObject.date(),
  month: momentObject.month() + monthOffset,
  year: momentObject.year()
})

/**
 * Create a standard JS Date from a proto compatible DateObject in UTC
 * @param {DateObject} dateObj - Proto compatible date
 * @return {Date} - JS date
 */
export const getDateFromUTCProtoDateObject = (dateObj: any) => {
  let date = null

  if (dateObj) {
    // XXX: DECREMENTING MONTH FOR MOMENT
    // moment months are zero-indexed; See: http://momentjs.com/docs/#/parsing/object
    const dateWithAdjustedMonth = { ...dateObj, month: dateObj.month - 1 }
    date = moment(dateWithAdjustedMonth).toDate()
  }

  return date
}

/**
 * Default Sunrise/Sunset options
 * @type {TimeObject[]}
 */
export const sunriseSunsetOptions = [
  createTimeObject(-4),
  createTimeObject(-3),
  createTimeObject(-2),
  createTimeObject(-1),
  createTimeObject(0),
  createTimeObject(1),
  createTimeObject(2),
  createTimeObject(3),
  createTimeObject(4)
]

/**
 * Get displayable start/end date string
 * @param {(date|string|number|null)} startDate - Start Date
 * @param {(date|string|number|null)} endDate - End Date
 * @param {string} timeZone - Device timeZone
 * @returns {string} - Start/End date string
 */
export const getStartEndDateString = (
  startDate?: ?(Date | string | number),
  endDate?: ?(Date | string | number),
  timeZone: string // NOTE: `timeZone` is for comparrison only, no conversion is done
) => {
  const formatStr = 'MMMM Do YYYY'
  const calendarOpts = {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    nextWeek: 'dddd',
    lastDay: '[Yesterday]',
    lastWeek: '[Last] dddd',
    sameElse: formatStr
  }
  let startDateStr = ''
  let endDateStr = 'Never'

  if (timeZone === moment.tz.guess()) {
    if (startDate) {
      startDateStr = moment(startDate).calendar(null, calendarOpts)
    }
    if (endDate) {
      endDateStr = moment(endDate).calendar(null, calendarOpts)
    }
  } else {
    if (startDate) {
      startDateStr = moment(startDate)
        .startOf('day')
        .format(momentFormatString)
    }
    if (endDate) {
      endDateStr = moment(endDate)
        .startOf('day')
        .format(momentFormatString)
    }
  }

  return startDateStr + ' - ' + endDateStr
}

/// Moment Utils //////////////////////////////////////////////////////////////

/** Default format string for moment objects */
export const momentFormatString = 'MMMM Do YYYY'

// Conversion /////////////////////////////////////////////////////////////////

/**
 * Get a JS date object in a specified timezone. Moment's `toDate()` always returns in localtimezone
 * @param  {string} timeZone - Desired timeZone
 * @param  {string|number} date - Desired date to based off of, may be `undefined`
 * @return {Date} - Date
 */
export const getJSDateInTimeZone = (
  timeZone: string,
  date?: string | number | Object
) =>
  new Date(
    moment(date)
      .tz(timeZone)
      .format('YYYY-MM-DD')
  )

/**
 * Reduce an array of seconds to single minute value
 * @param  {number[]} secondsArr - Array of seconds
 * @return {number} - Minutes
 */
export const getMinutesFromSeconds = (secondsArr: Array<number>) => {
  let minutes = 0
  const totalSeconds = secondsArr.reduce(
    (currentTotal, newValue) => currentTotal + newValue,
    0
  )
  minutes = Math.round(totalSeconds / 60)
  return minutes
}

/**
 * Convert seconds into an array of [hours, minutes]
 * @param  {number} seconds - Seconds to convert
 * @return {number[]} - [hours, minutes]
 */
export const getHoursAndMinutesFromSeconds = (
  seconds: number
): [number, number] => {
  const minutes = getMinutesFromSeconds([seconds])
  const hours = (minutes - (minutes % 60)) / 60

  return [hours, minutes % 60]
}

// Duration ///////////////////////////////////////////////////////////////////

export const dateAtTimeFormatString = 'MMM D [at] h:mm A'

const calendarFormattingOptions = {
  lastDay: '[Yesterday ]h:mm A',
  lastWeek: 'MM/DD/YYYY',
  sameDay: 'h:mm A',
  nextDay: '[Tomorrow ]h:mm A',
  sameElse: 'MM/DD/YYYY h:mm A'
}

/**
 * Create date/time string
 * @see {@link https://rachio.atlassian.net/browse/SC-2793}
 * @param {string} timeZone - Date/time string
 * @param  {string|number|ProtobufTimestamp} start - Start date/time
 * @param  {string|number} end - End date/time
 * @param  {bool} showDate - Whether a relative time string show be included (e.g. 'tomorrow', 'today')
 * @param  {object} [calendar] - Optional calendar formatting object; see https://momentjs.com/docs/#/customization/calendar/
 * @return {string} - Date/time string
 */
export const createDateTimeString = (
  timeZone: ?string,
  start: number | string | ProtobufTimestamp,
  end: ?(number | string),
  showDate?: boolean,
  calendar?: Object = calendarFormattingOptions
): string => {
  if (!timeZone) return ''

  const startMoment = moment.tz(start, timeZone)
  const startString = showDate
    ? startMoment.calendar(moment.tz(timeZone), {
        ...calendarFormattingOptions,
        ...calendar
      })
    : startMoment.format('h:mm A')

  return end
    ? startString + ' - ' + moment.tz(end, timeZone).format('h:mm A')
    : startString
}

/**
 * Create 'date' [at] 'time' formatted string
 * @see {@link https://rachio.atlassian.net/browse/SC-2793}
 * @param  {string} timeZone - date/time
 * @param  {string|number|ProtobufTimestamp} date - date/time
 * @return {string} - Date/time string
 */
export const createDateAtTimeFormatString = (
  timeZone: ?string,
  date: number | string | ProtobufTimestamp
): string => {
  if (!timeZone) return ''

  return moment.tz(date, timeZone).format(dateAtTimeFormatString)
}

/**
 * Create 'dayOfWeek, MM/DD' formatted string
 * @param  {string} timeZone - date/time
 * @param  {DateObject} dateObject - date/time
 * @param  {string} formatString - moment format string
 * @return {string} - Date/time string
 */
export const createFormattedString = (
  timeZone: ?string,
  dateObject: DateObject,
  formatString: string
): string => {
  if (!timeZone) return ''

  // XXX: DECREMENTING MONTH FOR MOMENT
  const dateWithAdjustedMonth = { ...dateObject, month: dateObject.month - 1 }

  return moment.tz(dateWithAdjustedMonth, timeZone).format(formatString)
}
